import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { LucideIcon } from 'lucide-react';
import { FIELD_THUMB_BOTTOM } from '@/components/layout/field-thumb-zone';
import { cn } from '@/lib/utils';

export interface FloatingAction {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  position?: 'left' | 'right' | 'center';
  variant?: 'default' | 'primary' | 'secondary';
  testId?: string;
}

interface FloatingActionContextType {
  registerAction: (action: FloatingAction | null) => void;
  currentAction: FloatingAction | null;
}

const FloatingActionContext = createContext<FloatingActionContextType | undefined>(undefined);

export function FloatingActionProvider({ children }: { children: ReactNode }) {
  const [currentAction, setCurrentAction] = useState<FloatingAction | null>(null);

  const registerAction = useCallback((action: FloatingAction | null) => {
    setCurrentAction(action);
  }, []);

  return (
    <FloatingActionContext.Provider value={{ registerAction, currentAction }}>
      {children}
    </FloatingActionContext.Provider>
  );
}

export function useFloatingAction() {
  const context = useContext(FloatingActionContext);
  if (!context) {
    throw new Error('useFloatingAction must be used within FloatingActionProvider');
  }
  return context;
}

/**
 * Renders the floating action button above the bottom navigation
 */
export function FloatingActionButton() {
  const { currentAction } = useFloatingAction();

  if (!currentAction) {
    return null;
  }

  const {
    icon: Icon,
    label,
    onClick,
    position = 'right',
    variant = 'primary',
    testId,
  } = currentAction;

  const positionClasses = {
    left: 'left-4',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-4',
  };

  const variantClasses = {
    default: 'bg-background border-2 border-border text-foreground hover:bg-muted',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'fixed z-[60]',
        FIELD_THUMB_BOTTOM,
        positionClasses[position],
        variantClasses[variant],
        'flex items-center gap-2 px-4 py-3 rounded-full shadow-lg',
        'transition-all active:scale-95 touch-manipulation',
        'font-medium text-sm min-h-11 min-w-11'
      )}
      data-testid={testId || 'floating-action-button'}
      style={{
        paddingBottom: `calc(0.75rem + env(safe-area-inset-bottom, 0px))`,
      }}
    >
      <Icon className="w-5 h-5" strokeWidth={2.5} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
